import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import './walletChargesSection.css';
import { SkeletonRows } from './SectionSkeleton';
import { useZohoOrgId, zohoPaymentUrl } from '../../../../utils/zohoOrg';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Zoho's UI route is #/paymentsreceived — #/customerpayments is only the API
// resource name and lands on nothing (owner, 2026-09-04).
const zohoReceiptUrl = (org, r) => zohoPaymentUrl(org, r.zoho_payment_id);

/* Card money handed back to the wallet (2026-10-16). The receipt's
   refunded_amount is the record of it; the wallet row it also writes no longer
   carries the booking id (that row was taking the same money off the booking a
   second time — booking 22786 read "Refunded 41.58" for a 20.79 return). A row
   written before the repair can still arrive here; it is shown, never summed. */
const CARD_RETURN_PREFIX = 'card payment returned to wallet';
const isCardReturnRow = (t) => t?.origin === 'card_return'
    || (t?.type === 'booking_refund' && String(t?.description || '').toLowerCase().startsWith(CARD_RETURN_PREFIX));

export default function WalletChargesSection({ bookingId, refreshTick = 0 }) {
    const zohoOrgId = useZohoOrgId();
    const [transactions, setTransactions] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [pending, setPending] = useState(0);
    // The rest of the pending payload: how this booking was actually settled.
    // Without it the card printed "Charged AED 0.00 · Pending AED x" on a
    // booking whose invoice was paid in full months ago (2026-08-30).
    const [paidInfo, setPaidInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!bookingId) return;
        let cancelled = false;
        (async () => {
            try {
                const [txRes, pcRes] = await Promise.all([
                    axios.get(`/api/wallet/bookings/${bookingId}/transactions`),
                    // Booking's still-pending amount (total − settled), so the Payments
                    // card states what the client still owes (2026-07-21).
                    axios.get(`/api/bookings/admin/${bookingId}/price-changes`).catch(() => null),
                ]);
                if (!cancelled) {
                    // The endpoint now returns { transactions, receipts }. Card
                    // booking-payments are RECEIPTS (their wallet round-trip is
                    // hidden server-side); wallet activity stays in transactions.
                    setTransactions(txRes.data?.transactions || []);
                    setReceipts(txRes.data?.receipts || []);
                    setPending(Number(pcRes?.data?.pending || 0));
                    setPaidInfo(pcRes?.data || null);
                }
            } catch {
                if (!cancelled) { setTransactions([]); setReceipts([]); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [bookingId, refreshTick]);

    const linkPayments = paidInfo?.link_payments || [];
    const paidOnInvoice = !!paidInfo?.paid_on_invoice;
    const overpaid = Number(paidInfo?.overpaid || 0);
    const linkIsGuest = !!paidInfo?.link_is_guest;

    // Keep the card when the booking was settled ON ITS INVOICE — that is a
    // payment, and hiding it is what left "no payment recorded" on paid work.
    // Hold the space while the two fetches land, rather than appearing out of
    // nowhere once they do (2026-08-30).
    /* The card mounts ONCE; only its body switches from shimmer to content
       (owner, 2026-09-02: "inside the white background container already"). */
    if (loading) {
        return (
            <section className="dp-card booking-section wcs">
                <h3 className="wcs__title">Payments</h3>
                <SkeletonRows rows={3} />
            </section>
        );
    }
    if (transactions.length === 0 && receipts.length === 0
        && linkPayments.length === 0 && !paidOnInvoice && pending <= 0.009) return null;

    const formatDate = (d) => {
        if (!d) return '—';
        return DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm');
    };

    // Card receipts count toward Charged/Refunded alongside wallet activity so
    // the summary reconciles with the card round-trip removed from the ledger.
    const walletCharged = transactions.filter(t => t.type === 'booking_charge').reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0);
    // Card money returned to the wallet is counted from the receipts below
    // (cardRefunded), never from its wallet row as well.
    const walletRefunded = transactions.filter(t => t.type === 'booking_refund' && !isCardReturnRow(t)).reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0);
    const hasCardReturnRow = transactions.some(isCardReturnRow);
    const cardCharged = receipts.reduce((s, r) => s + Math.abs(parseFloat(r.amount || 0)), 0);
    // Pay-link money is a payment for this booking like any other.
    const linkCharged = linkPayments.reduce((s, p) => s + Math.abs(parseFloat(p.amount || 0)), 0);
    const cardRefunded = receipts.reduce((s, r) => s + Math.abs(parseFloat(r.refunded_amount || 0)), 0);
    /* INVOICE-SETTLED MONEY IS STILL MONEY (owner, 2026-09-03: "why this one
       not showing the charged amount only the refund"). A booking settled on
       its PAID invoice carries no wallet/card/link rows of its own, so the
       tiles read Charged 0.00 — and after an edit refunds part of that money,
       Net goes negative on a fully paid booking. When the invoice settled it,
       what the client paid for this booking = its current total plus whatever
       was refunded back out of that invoice money. paid_on_invoice is only
       true when NOTHING was booking-attributed, so this never double-counts. */
    const settled = Math.max(0, Number(paidInfo?.settled || 0));
    // settled is NET of every return (wallet and card alike), so both come
    // back out of the gross figures before the remainder is called invoice money.
    const invoiceCharged = Math.max(0, Math.round(
        (settled - walletCharged - cardCharged - linkCharged + walletRefunded + cardRefunded) * 100
    ) / 100);
    const totalCharged = walletCharged + cardCharged + linkCharged + invoiceCharged;
    const totalRefunded = walletRefunded + cardRefunded;
    const netAmount = totalCharged - totalRefunded;

    // Wallet booking_charge / booking_refund lines. Card payments are no longer
    // relabeled wallet rows — they arrive as receipts and render separately.
    const getReason = (t) => {
        const desc = t.description || '';
        if (/^#?\d+ charge$/i.test(desc.trim())) return t.type === 'booking_charge' ? 'Wallet Payment' : 'Refund';
        if (desc.includes('Refund for deleted')) return 'Booking Deleted';
        if (t.type === 'booking_refund') return 'Refund';
        if (desc.includes('Type:') || desc.includes('Added') || desc.includes('Removed') ||
            desc.includes('Amenities') || desc.includes('Time changed') || desc.includes('Cleaners:') ||
            desc.includes('qty')) return 'Booking Change';
        if (desc.toLowerCase().includes('price increase')) return 'Price Increase';
        if (desc.toLowerCase().includes('price decrease')) return 'Price Decrease';
        return t.type === 'booking_charge' ? 'Wallet Payment' : 'Refund';
    };

    const getChangeDetails = (t) => {
        const desc = t.description || '';
        // New format: "#123: Type: X → Y, Added Z"
        const match = desc.match(/#\d+:\s*(.+)$/);
        if (match) {
            let details = match[1].replace(/\s*\([\d.]+\s*→\s*[\d.]+\)/, '').trim();
            if (details) return details.split(', ').filter(Boolean);
        }
        if (desc.toLowerCase().includes('price increase')) return ['Price went up'];
        if (desc.toLowerCase().includes('price decrease')) return ['Price went down'];
        return [];
    };

    return (
        <section className="dp-card booking-section wcs">
            <h3 className="wcs__title">Payments</h3>

            {/* MONEY IS OWED BACK. Loudest thing on the page, because on a guest
                pay link it cannot be settled by any automatic route — somebody
                has to refund a person who has no account here. */}
            {overpaid > 0.009 && (
                <div className={`wcs__alert${linkIsGuest ? ' wcs__alert--guest' : ''}`}>
                    <span className="wcs__alertIcon" aria-hidden="true">!</span>
                    <div className="wcs__alertBody">
                        <strong>
                            Already paid {money(totalCharged)} — the price is now {money(paidInfo?.total || 0)}.
                            {' '}{money(overpaid)} is owed back.
                        </strong>
                        <span>
                            {linkIsGuest
                                ? `Paid by a guest through a payment link${paidInfo?.link_guest_name ? ` (${paidInfo.link_guest_name})` : ''}. This must NOT go to the client's wallet — refund the guest directly.`
                                : 'Returned to the client\u2019s wallet automatically when the price was reduced. If this line persists, the refund did not complete — check the wallet.'}
                        </span>
                    </div>
                </div>
            )}

            {(paidOnInvoice || invoiceCharged > 0) && (
                <div className="wcs__paidNote">
                    Settled on invoice <strong>{paidInfo?.invoice_number || '—'}</strong> — the
                    charged figure is the invoice-side payment; this booking carries no
                    wallet or card payment of its own.
                </div>
            )}

            <div className="wcs__summary">
                <div className="wcs__summaryItem">
                    <span className="wcs__summaryLabel">Charged</span>
                    <span className="wcs__summaryValue wcs__summaryValue--charge">{money(totalCharged)}</span>
                </div>
                {totalRefunded > 0 && (
                    <div className="wcs__summaryItem">
                        <span className="wcs__summaryLabel">Refunded</span>
                        <span className="wcs__summaryValue wcs__summaryValue--refund">{money(totalRefunded)}</span>
                    </div>
                )}
                <div className="wcs__summaryItem">
                    <span className="wcs__summaryLabel">Net</span>
                    <span className="wcs__summaryValue">{money(netAmount)}</span>
                </div>
                {/* ALWAYS SHOWN, INCLUDING AT ZERO (2026-08-30). A missing tile
                    and a tile reading 0.00 are not the same statement: the
                    first leaves you wondering whether anything is outstanding,
                    the second answers it. Only the colour drops away when
                    there is nothing owing. */}
                <div className="wcs__summaryItem">
                    <span className="wcs__summaryLabel">Pending</span>
                    <span
                        className={`wcs__summaryValue ${pending > 0.009 ? 'wcs__summaryValue--pending' : ''}`}
                        title={pending > 0.009 ? "Billed on the client's next invoice" : 'Nothing outstanding on this booking'}
                    >
                        {money(pending)}
                    </span>
                </div>
            </div>

            <div className="wcs__list">
                {linkPayments.map((p) => (
                    <div key={`link-${p.id}`} className="wcs__item wcs__item--charge">
                        <div className="wcs__itemMain">
                            <div className="wcs__itemTop">
                                <span className="wcs__badge wcs__badge--card">Booking payment — payment link</span>
                                <span className="wcs__itemAmount wcs__summaryValue--charge">{money(p.amount)}</span>
                            </div>
                            <div className="wcs__itemMeta">
                                {formatDate(p.paid_at)}
                                {p.merchant_reference ? ` · ${p.merchant_reference}` : ''}
                                {p.payment_code ? ` · ${p.payment_code}` : ''}
                            </div>
                        </div>
                    </div>
                ))}
                {receipts.map((r) => {
                    const amt = Math.abs(parseFloat(r.amount || 0));
                    return (
                        <div key={`receipt-${r.id}`} className="wcs__item wcs__item--charge">
                            <div className="wcs__itemMain">
                                <div className="wcs__itemTop">
                                    {/* P1 — a payment FOR THIS BOOKING made by card. It is not a
                                        wallet top-up and never touched the wallet, so it must not
                                        be labelled (or read) as a deposit. */}
                                    <span className="wcs__badge wcs__badge--card">Booking payment — card</span>
                                    <span className="wcs__itemDate">{formatDate(r.gateway_captured_at || r.created_at)}</span>
                                    {r.zoho_payment_id && zohoOrgId && (
                                        <a
                                            className="wcs__receiptLink"
                                            href={zohoReceiptUrl(zohoOrgId, r)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            receipt ↗
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="wcs__itemAmount">
                                <span className="wcs__amount--charge">-{money(amt)}</span>
                            </div>
                        </div>
                    );
                })}
                {/* The card money this booking gave back to the client's wallet,
                    read from the receipt. Skipped while an older wallet row for
                    it is still in the list, so it is never shown twice. */}
                {!hasCardReturnRow && receipts
                    .filter((r) => Math.abs(parseFloat(r.refunded_amount || 0)) > 0.009)
                    .map((r) => (
                        <div key={`receipt-refund-${r.id}`} className="wcs__item wcs__item--refund">
                            <div className="wcs__itemMain">
                                <div className="wcs__itemTop">
                                    <span className="wcs__badge wcs__badge--refund">Card payment returned to wallet</span>
                                    <span className="wcs__itemDate">{formatDate(r.refunded_at || r.created_at)}</span>
                                </div>
                            </div>
                            <div className="wcs__itemAmount">
                                <span className="wcs__amount--refund">−{money(Math.abs(parseFloat(r.refunded_amount)))}</span>
                            </div>
                        </div>
                    ))}
                {transactions.map((t) => {
                    const isRefund = t.type === 'booking_refund';
                    const amt = Math.abs(parseFloat(t.amount));
                    const details = getChangeDetails(t);
                    const cashAmt = Math.abs(parseFloat(t.cash_amount || 0));
                    const bonusAmt = Math.abs(parseFloat(t.bonus_amount || 0));

                    return (
                        <div key={t.id} className={`wcs__item ${isRefund ? 'wcs__item--refund' : 'wcs__item--charge'}`}>
                            <div className="wcs__itemMain">
                                <div className="wcs__itemTop">
                                    <span className={`wcs__badge ${isRefund ? 'wcs__badge--refund' : 'wcs__badge--charge'}`}>
                                        {getReason(t)}
                                    </span>
                                    <span className="wcs__itemDate">{formatDate(t.created_at)}</span>
                                    {t.created_by_name && <span className="wcs__itemBy">{t.created_by_name}</span>}
                                </div>
                                {details.length > 0 && (
                                    <div className="wcs__details">
                                        {details.map((d, i) => <span key={i} className="wcs__detail">{d}</span>)}
                                    </div>
                                )}
                                {(bonusAmt > 0) && (
                                    <div className="wcs__splitInfo">
                                        Cash: {money(cashAmt)} · Bonus: {money(bonusAmt)}
                                    </div>
                                )}
                            </div>
                            <div className="wcs__itemAmount">
                                {/* Sign matches the colour and the audience: this
                                    is the office's page, so + is money we took
                                    and − is money we gave back. It read the
                                    other way round because the signs were the
                                    client's wallet's (2026-08-30). */}
                                <span className={isRefund ? 'wcs__amount--refund' : 'wcs__amount--charge'}>
                                    {isRefund ? '−' : '+'}{money(amt)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
